import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { Formik } from 'formik';
import { useCallback } from 'react';
import { vendorsApi } from 'src/app/services/Vendors/Vendors.service';
import {
  type FormVendorProductLeadBillingInfoValues,
  toVendorLeadBillinInfoInitialValues,
  vendorProductLeadBillingInfoPublishValidationSchema,
} from 'src/components/vendors/billing/VendorLeadBillingInfo.validation';
import { VendorLeadBillingInfoContent } from 'src/components/vendors/billing/VendorLeadBillingInfoContent';
import { VendorLeadBillingInfoLayout } from 'src/components/vendors/billing/VendorLeadBillingInfoLayout';
import type { IVendorLeadBillingInfo } from 'src/types/vendors/IVendorLeadBillingInfo';

type VendorLeadBillingInfoProps = {
  vendorLocationId: string;
  vendorLeadBillingInfo: IVendorLeadBillingInfo;

  onSubmit: () => void;
};

export function VendorLeadBillingInfo({
  vendorLocationId,
  vendorLeadBillingInfo,
  onSubmit,
}: Readonly<VendorLeadBillingInfoProps>) {
  const [, updateVendorLeadBillingInfo] = vendorsApi.useUpdateVendorLeadBillingInfoMutation(vendorLocationId);

  const save = useCallback(
    async (newValues: FormVendorProductLeadBillingInfoValues) => {
      await updateVendorLeadBillingInfo({
        data: {
          paymentTerms: newValues.paymentTerms,
          paymentMethod: newValues.paymentMethod,
          paymentContact: newValues.paymentContact,

          billingAddress: newValues.billingAddress,
          bankAccount: {
            accountNumber: newValues.bankAccountNumber ?? '',
            routingNumber: newValues.routingNumber ?? '',
          },
        },
      });
    },
    [updateVendorLeadBillingInfo]
  );

  const handleSubmit = useCallback(
    async (submitValues: FormVendorProductLeadBillingInfoValues) => {
      Modal.confirm({
        centered: true,
        maskClosable: true,
        title: 'Confirm submitting',
        content: `Once you submit you won't be able to change it.\nDo you confirm it?`,
        icon: <ExclamationCircleOutlined />,
        okText: 'Confirm',
        okType: 'primary',
        onOk: async () => {
          await save(submitValues);

          onSubmit();
        },
      });
    },
    [save, onSubmit]
  );

  return (
    <Formik<FormVendorProductLeadBillingInfoValues>
      initialValues={toVendorLeadBillinInfoInitialValues(vendorLeadBillingInfo)}
      validationSchema={vendorProductLeadBillingInfoPublishValidationSchema}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <VendorLeadBillingInfoLayout>
          <VendorLeadBillingInfoContent formikProps={formikProps} />
        </VendorLeadBillingInfoLayout>
      )}
    </Formik>
  );
}
